import { Info, type LucideProps } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { cn } from "@/lib/utils";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { Separator } from "./separator";

const headingVariants = cva("font-semibold tracking-tight", {
  variants: {
    level: {
      one: "text-xl lg:text-2xl",
      two: "text-lg lg:text-xl",
    },
  },
  defaultVariants: {
    level: "one",
  },
});
const textVariants = cva("tracking-tight text-muted-foreground", {
  variants: {
    level: {
      one: "text-md",
      two: "text-sm",
    },
  },
  defaultVariants: {
    level: "one",
  },
});

const iconVariants = cva("", {
  variants: {
    level: {
      one: "h-10, w-10",
      two: "h-7 w-7",
    },
  },
  defaultVariants: {
    level: "one",
  },
});

type PageHeaderProps = {
  className?: string;
  heading: string;
  text?: React.ReactNode;
  tooltip?: React.ReactNode;
  icon?: React.FC<LucideProps>;
  separator?: boolean;
} & VariantProps<typeof headingVariants>;

export function PageHeader({
  className,
  heading,
  text,
  tooltip,
  level,
  icon,
  separator = false,
}: PageHeaderProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-center gap-2">
        {icon &&
          React.createElement(icon, {
            className: iconVariants({ level }),
          })}
        <h2 className={headingVariants({ level })}>{heading}</h2>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-5 w-5 text-primary" />
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {text && typeof text === "string" ? (
        <p className={textVariants({ level })}>{text}</p>
      ) : (
        text
      )}
      {separator && <Separator className="mb-2 mt-2" />}
    </div>
  );
}
